import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle, ClipboardCheck, MessageCircle, Search,
  Sparkles, UserCheck, User, Users, Timer, BookOpen, type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Shared building blocks for the static service pages.
 * Styled after the designer HTML brief so all marketing pages read as one family.
 * Copy lives as data in each page; these components only decide layout and style.
 */

// ─── Design tokens (mirrors designer CSS vars) ───────────────────────────────
const NAVY   = "hsl(237,52%,31%)";     // --navy / --indigo
const AMBER  = "hsl(39,96%,48%)";      // --amber
const LAV    = "hsl(237,30%,95%)";     // --lav-100 tint

// Designer spec: primary = amber pill, ghost = navy outline pill
export const PRIMARY_BTN =
  "rounded-full px-7 py-3.5 bg-[#F7B030] hover:bg-[#F29F05] text-[#262A7A] font-semibold shadow-md hover:-translate-y-0.5 transition-all";
const GHOST_BTN =
  "rounded-full px-7 py-3.5 border-2 border-[#262A7A] text-[#262A7A] hover:bg-[#EEEFF9] font-semibold transition-colors";

// ─── Step tile icons & gradients ─────────────────────────────────────────────
const STEP_TILES: { icon: LucideIcon; gradient: string }[] = [
  { icon: Search,       gradient: "from-[hsl(39,96%,48%)] to-[hsl(30,90%,45%)]" },
  { icon: UserCheck,    gradient: "from-[hsl(260,60%,55%)] to-[hsl(280,55%,50%)]" },
  { icon: ClipboardCheck, gradient: "from-[hsl(35,95%,55%)] to-[hsl(25,90%,50%)]" },
  { icon: MessageCircle, gradient: "from-[hsl(340,70%,55%)] to-[hsl(310,60%,50%)]" },
  { icon: BookOpen,     gradient: "from-[hsl(200,70%,50%)] to-[hsl(220,65%,45%)]" },
  { icon: Sparkles,     gradient: "from-[hsl(160,65%,40%)] to-[hsl(180,60%,35%)]" },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
function Eyebrow({ children, light }: { children: ReactNode; light?: boolean }) {
  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest mb-4 px-3 py-1 rounded-full",
      light
        ? "text-[hsl(237,52%,31%)] bg-white/80 border border-[hsl(237,52%,31%)]/20 backdrop-blur-sm"
        : "text-[hsl(237,52%,40%)] bg-[hsl(237,52%,31%)]/10",
    )}>
      <Sparkles className="w-3 h-3" />
      {children}
    </span>
  );
}

// Amber swoosh under the H1 highlight — matches designer's SWOOSH SVG
function Swoosh() {
  return (
    <svg className="absolute -bottom-1 sm:-bottom-2 left-0 w-full h-3 sm:h-4" viewBox="0 0 320 40" aria-hidden="true" preserveAspectRatio="none">
      <path d="M4 18 C 90 8, 210 6, 316 10 L 312 16 C 210 14, 100 18, 8 26 Z" fill="hsl(39,96%,55%)" />
      <path d="M40 32 C 120 26, 220 24, 296 26 L 292 31 C 220 31, 130 34, 44 38 Z" fill="hsl(39,96%,48%)" />
    </svg>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────
export interface CtaLink { label: string; to: string; }
export interface FaqItem { question: string; answer: string; }
/** A delivered photo; width/height are its intrinsic size (sets aspect ratio, avoids layout shift). */
export interface SectionImageData { src: string; alt: string; width: number; height: number; }

export interface FactBadge {
  icon: LucideIcon;
  label: string;
}

export interface DefItem {
  /** Lucide icon name equivalent (pass the component) */
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface ContentSectionData {
  id?: string;
  title: string;
  level?: "h2" | "h3";
  intro?: ReactNode[];
  bullets?: ReactNode[];
  ordered?: boolean;
  outro?: ReactNode[];
  /** Banner photo under the heading */
  image?: SectionImageData;
}

export interface RelatedPage {
  label: string;
  color: string;        // Tailwind bg class or hex
  icon: LucideIcon;
  title: string;
  excerpt: string;
  to: string;
}

// ─── Jump Navigation ──────────────────────────────────────────────────────────
export interface JumpItem { label: string; id: string; }

export function JumpNav({ items }: { items: JumpItem[] }) {
  return (
    <nav
      aria-label="On this page"
      className="sticky top-[152px] z-30 bg-white/95 backdrop-blur-sm border-b border-[#DCDDEE] hidden lg:block"
    >
      <div className="container mx-auto px-4 flex items-center gap-1 h-11 overflow-x-auto scrollbar-none">
        {items.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className="whitespace-nowrap text-sm font-medium text-[#5A5E80] hover:text-[hsl(237,52%,31%)] hover:bg-[hsl(237,52%,31%)]/8 px-3 py-2 rounded-full transition-colors flex-none"
          >
            {item.label}
          </a>
        ))}
      </div>
    </nav>
  );
}

// ─── Service Hero ─────────────────────────────────────────────────────────────
export function ServiceHero({
  badge, badgeColor, titleLead, titleHighlight, intro, primary, secondary, facts, image,
}: {
  badge: string;
  /** Hex colour for the format pill — #2F6BD8 (1-on-1) | #0E8A78 (group) | #D9472B (crash) */
  badgeColor?: string;
  titleLead: string;
  titleHighlight: string;
  intro: string;
  primary: CtaLink;
  secondary: CtaLink;
  facts?: FactBadge[];
  image?: string;
}) {
  return (
    <section
      className="relative pt-28 pb-16 sm:pt-36 lg:pt-44 sm:pb-24 overflow-hidden"
      style={{ background: "linear-gradient(180deg,#fff 0%,#FFF8EA 70%,#fff 100%)" }}
    >
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Designer spec: 1.05fr text / 0.95fr image, 60px gap */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center">

          {/* ── Left: text ── */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            {/* Format pill — coloured background, white text, icon */}
            <span
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-1.5 rounded-full text-white mb-4"
              style={{ background: badgeColor ?? "#2F6BD8" }}
            >
              {badge}
            </span>

            <h1 className="font-display text-[2rem] sm:text-[2.75rem] lg:text-[3.375rem] text-[#262A7A] mb-1.5 leading-[1.08] font-semibold">
              {titleLead}{" "}
              <span className="relative inline-block pb-3">
                {titleHighlight}
                {/* Two-line amber underline — matches the "My" underline in the BMC wordmark */}
                <svg className="absolute bottom-0 left-0 w-full h-[14px]" viewBox="0 0 300 20" fill="none" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M6 9 C 80 3, 220 3, 294 9" stroke="#F7B030" strokeWidth="2.5" strokeLinecap="round" />
                  <path d="M22 16 C 90 11, 210 11, 278 16" stroke="#F29F05" strokeWidth="1.5" strokeLinecap="round" opacity="0.65" />
                </svg>
              </span>
            </h1>

            <p className="text-[#5A5E80] text-base sm:text-lg leading-relaxed mb-7 mt-5 max-w-xl">{intro}</p>

            {/* CTAs: amber primary, ghost secondary — designer spec */}
            <div className="flex flex-col sm:flex-row gap-3 mb-7 w-full sm:w-auto justify-center lg:justify-start">
              <Link
                to={primary.to}
                className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 bg-[#F7B030] hover:bg-[#F29F05] text-[#262A7A] font-semibold text-base transition-colors shadow-md"
              >
                {primary.label}
                <ArrowRight className="w-4 h-4" />
              </Link>
              {secondary.to.startsWith("#") ? (
                <a
                  href={secondary.to}
                  className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 border-2 border-[#262A7A] text-[#262A7A] hover:bg-[#EEEFF9] font-semibold text-base transition-colors"
                >
                  {secondary.label}
                </a>
              ) : (
                <Link
                  to={secondary.to}
                  className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 border-2 border-[#262A7A] text-[#262A7A] hover:bg-[#EEEFF9] font-semibold text-base transition-colors"
                >
                  {secondary.label}
                </Link>
              )}
            </div>

            {/* Fact pills */}
            {facts && facts.length > 0 && (
              <ul className="flex flex-wrap gap-2.5 justify-center lg:justify-start">
                {facts.map((f) => (
                  <li
                    key={f.label}
                    className="inline-flex items-center gap-2 bg-white border border-[#DCDDEE] rounded-full px-4 py-1.5 text-[15px] font-medium text-[#1D2045]"
                  >
                    <f.icon className="w-4 h-4 text-[#363A96] flex-shrink-0" />
                    {f.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Right: image / placeholder ── */}
          <div className={image && (image.startsWith("/") || image.startsWith("http")) ? "relative" : "hidden lg:block relative"}>
            {/* Decorative rings — desktop only */}
            <div className="hidden lg:block absolute -top-16 -right-16 w-[300px] h-[300px] rounded-full border-[22px] border-[#EEEFF9] pointer-events-none" />
            <div className="hidden lg:block absolute -bottom-16 right-[100px] w-[260px] h-[260px] rounded-full border-[22px] border-[#FFF1D6] pointer-events-none" />

            <div className={cn(
              "relative z-10 w-full rounded-3xl bg-gradient-to-br from-[#EEEFF9] via-white to-[#FFF1D6] border border-[#DCDDEE] shadow-xl overflow-hidden flex items-center justify-center",
              image && (image.startsWith("/") || image.startsWith("http")) ? "aspect-video" : "aspect-[6/5]",
            )}>
              {image && (image.startsWith("/") || image.startsWith("http")) ? (
                <img src={image} alt={badge} className="w-full h-full object-cover" />
              ) : image ? (
                <p className="text-xs text-[#A9ACD6] px-8 text-center leading-relaxed">📷 {image}</p>
              ) : (
                <span className="text-5xl">🎓</span>
              )}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

// ─── Content Section (original simple layout) ─────────────────────────────────
export function ContentSection({ section, shaded }: { section: ContentSectionData; shaded?: boolean }) {
  const Heading = section.level ?? "h2";
  return (
    <section id={section.id} className={cn("py-14 sm:py-[88px] scroll-mt-28", shaded ? "bg-[hsl(237,52%,97%)]" : "bg-background")}>
      <div className="container mx-auto px-4 max-w-3xl">
        <Heading className={cn(
          "font-sans text-foreground text-center",
          Heading === "h2" ? "text-2xl sm:text-4xl mb-6 sm:mb-8" : "text-xl sm:text-3xl mb-5 sm:mb-6",
        )}>
          {section.title}
        </Heading>

        {section.image && <SectionImage image={section.image} className="mb-6" />}

        {section.intro?.map((p, i) => (
          <p key={i} className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-4">{p}</p>
        ))}

        {section.bullets && (section.ordered ? (
          <div className="relative my-8">
            <div className="absolute left-7 sm:left-[2.75rem] top-10 bottom-10 w-px bg-gradient-to-b from-[hsl(237,52%,40%)]/40 via-[hsl(237,52%,40%)]/20 to-transparent" />
            <ol className="relative flex flex-col gap-4 sm:gap-6">
              {section.bullets.map((b, i) => {
                const tile = STEP_TILES[i % STEP_TILES.length];
                return (
                  <li key={i} className="flex gap-4 sm:gap-6 items-center">
                    <div className={cn(
                      "flex-shrink-0 z-10 w-14 h-14 sm:w-[5.5rem] sm:h-[5.5rem] rounded-2xl flex flex-col items-center justify-center bg-gradient-to-br shadow-lg",
                      tile.gradient,
                    )}>
                      <tile.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white sm:mb-1" />
                      <span className="text-[10px] sm:text-xs font-bold text-white/70 uppercase tracking-widest">Step {i + 1}</span>
                    </div>
                    <div className="flex-1 rounded-2xl border border-border/40 bg-card p-4 sm:p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">
                      <p className="font-sans text-base sm:text-lg text-foreground">{b}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        ) : (
          <ul className="rounded-2xl border border-border/40 bg-card p-5 sm:p-6 flex flex-col gap-3 my-6">
            {section.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-[hsl(237,52%,40%)] mt-0.5 flex-shrink-0" />
                <span className="text-sm sm:text-base text-foreground leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        ))}

        {section.outro?.map((p, i) => (
          <p key={i} className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-4 last:mb-0">{p}</p>
        ))}
      </div>
    </section>
  );
}

// ─── Split Section (image + text, supports defs, left/right image) ────────────
export function SplitSection({
  id, title, level = "h2", imageLeft = false, shaded = false,
  paras = [], bullets = [], paras2 = [], defs = [], after = [], cta,
  imagePlaceholder, image,
}: {
  id?: string;
  title: string;
  level?: "h2" | "h3";
  imageLeft?: boolean;
  shaded?: boolean;
  paras?: ReactNode[];
  bullets?: string[];
  paras2?: ReactNode[];
  defs?: DefItem[];
  after?: ReactNode[];
  cta?: CtaLink;
  /** Alt text for the image placeholder */
  imagePlaceholder?: string;
  /** Real photo; replaces the placeholder when set */
  image?: SectionImageData;
}) {
  const Heading = level;
  return (
    <section
      id={id}
      className={cn("py-14 sm:py-[88px] scroll-mt-28", shaded ? "bg-[hsl(237,52%,97%)]" : "bg-background")}
    >
      <div className="container mx-auto px-4">
        <div className={cn(
          // A real photo stays half-and-half from tablet width up; placeholders keep the lg split.
          "grid grid-cols-1 gap-10 lg:gap-16 items-center",
          image ? "md:grid-cols-2" : "lg:grid-cols-2",
          imageLeft && (image ? "md:[&>*:first-child]:order-last" : "lg:[&>*:first-child]:order-last"),
        )}>
          {/* Text */}
          <div>
            <Heading className={cn(
              "font-sans text-[#262A7A]",
              Heading === "h2" ? "text-2xl sm:text-4xl mb-5" : "text-xl sm:text-3xl mb-4",
            )}>
              {title}
            </Heading>

            {paras.map((p, i) => (
              <p key={i} className="text-[#5A5E80] text-sm sm:text-base leading-relaxed mb-3">{p}</p>
            ))}

            {bullets.length > 0 && (
              <ul className="flex flex-col gap-2.5 my-5">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-[hsl(237,52%,40%)] mt-0.5 flex-shrink-0" />
                    <span className="text-sm sm:text-base text-[#262A7A] leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            )}

            {defs.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-5">
                {defs.map((d, i) => (
                  <div key={i} className="flex gap-3">
                    <d.icon className="w-5 h-5 text-[hsl(237,52%,40%)] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-[#262A7A] text-sm">{d.title}</p>
                      <p className="text-[#5A5E80] text-sm leading-snug mt-0.5">{d.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {paras2.map((p, i) => (
              <p key={i} className="text-[#5A5E80] text-sm sm:text-base leading-relaxed mb-3">{p}</p>
            ))}

            {after.map((p, i) => (
              <p key={i} className="text-[#5A5E80] text-sm sm:text-base leading-relaxed mb-3">{p}</p>
            ))}

            {cta && (
              <Button asChild size="sm" className={cn(PRIMARY_BTN, "mt-4")}>
                <Link to={cta.to}>{cta.label} <ArrowRight className="w-4 h-4 ml-1" /></Link>
              </Button>
            )}
          </div>

          {/* Image (or placeholder) */}
          <div className={cn("lg:sticky lg:top-44")}>
            {image ? <SectionImage image={image} /> : <ImagePlaceholder alt={imagePlaceholder ?? title} />}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Steps Section ────────────────────────────────────────────────────────────
export function StepsSection({
  id, title, level = "h2", intro, steps, after, cta, shaded = false, image,
}: {
  id?: string;
  title: string;
  level?: "h2" | "h3";
  intro?: string;
  steps: { title: string; description: string }[];
  after?: ReactNode[];
  cta?: CtaLink;
  shaded?: boolean;
  /** Photo beside the steps (steps then stack in two columns) */
  image?: SectionImageData;
}) {
  const Heading = level;
  const cols = image ? 2 : steps.length <= 3 ? steps.length : 4;
  const stepList = (
    <ol className={cn(
      "grid gap-6",
      cols === 3 ? "sm:grid-cols-3" : cols === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : `sm:grid-cols-${cols}`,
    )}>
      {steps.map((step, i) => (
        <li key={i} className="relative pl-0">
          {/* Connector line */}
          {!image && i < steps.length - 1 && (
            <div className="hidden lg:block absolute top-6 left-[calc(100%)] w-full h-px border-t-2 border-dashed border-[hsl(237,30%,75%)] z-0" style={{ width: "calc(100% - 48px)", left: "48px" }} />
          )}
          <div className="w-12 h-12 rounded-full bg-[hsl(39,96%,48%)] text-[hsl(237,52%,20%)] font-bold text-xl flex items-center justify-center mb-4 relative z-10">
            {i + 1}
          </div>
          <h3 className="font-sans font-semibold text-[#262A7A] text-base sm:text-lg mb-1.5">{step.title}</h3>
          <p className="text-[#5A5E80] text-sm sm:text-base leading-relaxed">{step.description}</p>
        </li>
      ))}
    </ol>
  );
  return (
    <section id={id} className={cn("py-14 sm:py-[88px] scroll-mt-28", shaded ? "bg-[hsl(237,52%,97%)]" : "bg-background")}>
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mb-10">
          <Heading className={cn("font-sans text-[#262A7A]", Heading === "h2" ? "text-2xl sm:text-4xl mb-4" : "text-xl sm:text-3xl mb-3")}>
            {title}
          </Heading>
          {intro && <p className="text-[#5A5E80] text-base sm:text-lg leading-relaxed">{intro}</p>}
        </div>

        {image ? (
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-14 items-start">
            <div className="lg:sticky lg:top-44"><SectionImage image={image} /></div>
            {stepList}
          </div>
        ) : stepList}

        {after && after.map((p, i) => (
          <p key={i} className="text-[#5A5E80] text-sm sm:text-base leading-relaxed mt-6 max-w-3xl">{p}</p>
        ))}

        {cta && (
          <Button asChild size="lg" className={cn(PRIMARY_BTN, "mt-8")}>
            <Link to={cta.to}>{cta.label} <ArrowRight className="w-4 h-4 ml-2" /></Link>
          </Button>
        )}
      </div>
    </section>
  );
}

// ─── Cards Section ────────────────────────────────────────────────────────────
export interface CardItem {
  icon: LucideIcon;
  title: string;
  description: string;
}

export function CardsSection({
  id, title, level = "h2", intro, cards, cols = 3, after, shaded = false, imageSide, image,
}: {
  id?: string;
  title: string;
  level?: "h2" | "h3";
  intro?: string;
  cards: CardItem[];
  cols?: 2 | 3 | 4;
  after?: ReactNode[];
  shaded?: boolean;
  /** When set, renders image on the given side with cards on the other */
  imageSide?: "left" | "right";
  /** Real photo for the image side; placeholder when unset */
  image?: SectionImageData;
}) {
  const Heading = level;

  const cardGrid = (
    <div className="grid grid-cols-2 gap-3">
      {cards.map((card, i) => (
        <div
          key={i}
          className={cn(
            "rounded-xl border p-4",
            shaded ? "bg-white border-[#DCDDEE]" : "bg-[hsl(237,52%,97%)] border-[hsl(237,30%,88%)]",
          )}
        >
          <div className="w-9 h-9 rounded-lg bg-[hsl(237,52%,31%)] text-[hsl(39,96%,55%)] flex items-center justify-center mb-2.5">
            <card.icon className="w-4 h-4" />
          </div>
          <h3 className="font-sans font-semibold text-[#262A7A] text-sm mb-1">{card.title}</h3>
          <p className="text-[#5A5E80] text-xs leading-relaxed">{card.description}</p>
        </div>
      ))}
    </div>
  );

  if (imageSide) {
    return (
      <section id={id} className={cn("py-14 sm:py-[88px] scroll-mt-28", shaded ? "bg-[hsl(237,52%,97%)]" : "bg-background")}>
        <div className="container mx-auto px-4">
          <div className={cn(
            // A real photo sits beside the heading from tablet width up; placeholders keep the lg split.
            "grid grid-cols-1 gap-10 lg:gap-16 items-start",
            image ? "md:grid-cols-2" : "lg:grid-cols-2",
            imageSide === "right" && (image ? "md:[&>*:first-child]:order-last" : "lg:[&>*:first-child]:order-last"),
          )}>
            {/* Image side */}
            <div className={image ? "md:sticky md:top-44" : "lg:sticky lg:top-44"}>
              {image ? <SectionImage image={image} /> : <ImagePlaceholder alt={title} />}
            </div>

            {/* Text + cards side */}
            <div>
              <Heading className={cn("font-sans text-[#262A7A]", Heading === "h2" ? "text-2xl sm:text-4xl mb-4" : "text-xl sm:text-3xl mb-3")}>
                {title}
              </Heading>
              {intro && <p className="text-[#5A5E80] text-base leading-relaxed mb-6">{intro}</p>}
              {cardGrid}
              {after && after.map((p, i) => (
                <p key={i} className="text-[#5A5E80] text-sm sm:text-base leading-relaxed mt-5 max-w-2xl">{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id={id} className={cn("py-14 sm:py-[88px] scroll-mt-28", shaded ? "bg-[hsl(237,52%,97%)]" : "bg-background")}>
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mb-10">
          <Heading className={cn("font-sans text-[#262A7A]", Heading === "h2" ? "text-2xl sm:text-4xl mb-4" : "text-xl sm:text-3xl mb-3")}>
            {title}
          </Heading>
          {intro && <p className="text-[#5A5E80] text-base sm:text-lg leading-relaxed">{intro}</p>}
        </div>

        <div className={cn(
          "grid gap-5",
          cols === 2 ? "sm:grid-cols-2" : cols === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2 lg:grid-cols-3",
        )}>
          {cards.map((card, i) => (
            <div
              key={i}
              className={cn(
                "rounded-2xl border p-6",
                shaded ? "bg-white border-[#DCDDEE]" : "bg-[hsl(237,52%,97%)] border-[hsl(237,30%,88%)]",
              )}
            >
              <div className="w-12 h-12 rounded-xl bg-[hsl(237,52%,31%)] text-[hsl(39,96%,55%)] flex items-center justify-center mb-4">
                <card.icon className="w-5 h-5" />
              </div>
              <h3 className="font-sans font-semibold text-[#262A7A] text-base mb-2">{card.title}</h3>
              <p className="text-[#5A5E80] text-sm leading-relaxed">{card.description}</p>
            </div>
          ))}
        </div>

        {after && after.map((p, i) => (
          <p key={i} className="text-[#5A5E80] text-sm sm:text-base leading-relaxed mt-6 max-w-3xl">{p}</p>
        ))}
      </div>
    </section>
  );
}

// ─── Panel Section (two-tone: image + checklist) ──────────────────────────────
export function PanelSection({
  id, title, level = "h3", intro, paras = [], items, boxTitle, panelLink, shaded = false,
  badges, image,
}: {
  id?: string;
  title: string;
  level?: "h2" | "h3";
  intro?: string;
  paras?: ReactNode[];
  items: string[];
  boxTitle?: string;
  panelLink?: CtaLink;
  shaded?: boolean;
  badges?: boolean;
  /** Real photo above the heading (shown at every width); placeholder when unset */
  image?: SectionImageData;
}) {
  const Heading = level;
  return (
    <section id={id} className={cn("py-14 sm:py-[88px] scroll-mt-28", shaded ? "bg-[hsl(237,52%,97%)]" : "bg-background")}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 rounded-2xl overflow-hidden border border-[#DCDDEE] shadow-sm">
          {/* Left — image + heading + copy */}
          <div className="p-8 sm:p-10 bg-white">
            {image ? (
              <SectionImage image={image} className="mb-6 rounded-xl" />
            ) : (
              <div className="hidden sm:block">
                <ImagePlaceholder alt={title} className="mb-6 rounded-xl" small />
              </div>
            )}
            <Heading className={cn("font-sans text-[#262A7A] mb-3", Heading === "h2" ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl")}>
              {title}
            </Heading>
            {intro && <p className="text-[#5A5E80] text-base leading-relaxed mt-2">{intro}</p>}
            {paras.map((p, i) => (
              <p key={i} className="text-[#5A5E80] text-sm sm:text-base leading-relaxed mt-3">{p}</p>
            ))}
          </div>

          {/* Right — navy, checklist */}
          <div className="p-8 sm:p-10 bg-[hsl(237,52%,25%)] text-[hsl(237,20%,85%)]">
            {badges && (
              <div className="flex gap-2 mb-5">
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-[hsl(39,96%,48%)] text-[hsl(237,52%,20%)] text-xs font-bold">Gold</span>
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/20 text-white text-xs font-bold">Platinum</span>
              </div>
            )}
            {boxTitle && (
              <p className="font-semibold text-[hsl(39,96%,60%)] text-sm mb-4">{boxTitle}</p>
            )}
            <ul className="flex flex-col gap-3">
              {items.map((item, i) => {
                const [t, ...rest] = item.split(": ");
                return (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-[hsl(39,96%,55%)] flex-shrink-0 mt-0.5" />
                    <span className="text-sm leading-relaxed">
                      {rest.length > 0 ? <><strong className="text-white">{t}:</strong> {rest.join(": ")}</> : item}
                    </span>
                  </li>
                );
              })}
            </ul>
            {panelLink && (
              <Link to={panelLink.to} className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-[hsl(39,96%,60%)] hover:underline">
                {panelLink.label} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Compare Section ──────────────────────────────────────────────────────────
export interface CompareCard {
  icon: LucideIcon;
  title: string;
  description: string;
  current?: boolean;
  link?: CtaLink;
  /** Photo across the top of the card */
  image?: SectionImageData;
}

export function CompareSection({
  id, title, level = "h3", intro, cards, after, shaded = false,
}: {
  id?: string;
  title: string;
  level?: "h2" | "h3";
  intro?: string;
  cards: CompareCard[];
  after?: ReactNode[];
  shaded?: boolean;
}) {
  const Heading = level;
  return (
    <section id={id} className={cn("py-14 sm:py-[88px] scroll-mt-28", shaded ? "bg-[hsl(237,52%,97%)]" : "bg-background")}>
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mb-8">
          <Heading className={cn("font-sans text-[#262A7A]", Heading === "h2" ? "text-2xl sm:text-4xl mb-3" : "text-xl sm:text-3xl mb-3")}>
            {title}
          </Heading>
          {intro && <p className="text-[#5A5E80] text-base leading-relaxed">{intro}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl">
          {cards.map((card, i) => (
            <div
              key={i}
              className={cn(
                "rounded-2xl border p-6 bg-white relative overflow-hidden",
                card.current
                  ? "border-[hsl(237,52%,31%)] shadow-lg shadow-[hsl(237,52%,31%)]/15 ring-1 ring-[hsl(237,52%,31%)]"
                  : "border-[#DCDDEE]",
              )}
            >
              {card.image && <SectionImage image={card.image} className="-mx-6 -mt-6 mb-5 w-[calc(100%+3rem)] max-w-none rounded-none" />}
              {card.current && (
                <span className="absolute z-10 top-4 left-4 bg-[hsl(39,96%,48%)] text-[hsl(237,52%,20%)] text-xs font-bold px-2.5 py-1 rounded-full">
                  This format
                </span>
              )}
              <div className={cn("flex items-center gap-3 mb-3", card.current && !card.image && "mt-6")}>
                <card.icon className="w-6 h-6 text-[hsl(237,52%,40%)]" />
                <h3 className="font-sans font-semibold text-[#262A7A] text-lg">{card.title}</h3>
              </div>
              <p className="text-[#5A5E80] text-sm leading-relaxed">{card.description}</p>
              {card.link && (
                <Link to={card.link.to} className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-[hsl(237,52%,40%)] hover:underline">
                  {card.link.label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          ))}
        </div>

        {after && after.map((p, i) => (
          <p key={i} className="text-[#5A5E80] text-sm sm:text-base leading-relaxed mt-6 max-w-3xl">{p}</p>
        ))}
      </div>
    </section>
  );
}

// ─── Formats Section (3 format cards with colored top border) ─────────────────
export interface FormatCard {
  icon: LucideIcon;
  title: string;
  description: string;
  /** Tailwind text & border colour class e.g. "text-blue-600 border-blue-500" */
  colorClass: string;
  borderColor: string;
}

export function FormatsSection({
  id, title, intro, formats, after, shaded = false,
}: {
  id?: string;
  title: string;
  intro?: string;
  formats: FormatCard[];
  after?: ReactNode[];
  shaded?: boolean;
}) {
  return (
    <section id={id} className={cn("py-14 sm:py-[88px] scroll-mt-28", shaded ? "bg-[hsl(237,52%,97%)]" : "bg-background")}>
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mb-10">
          <h2 className="font-sans text-2xl sm:text-4xl text-[#262A7A] mb-4">{title}</h2>
          {intro && <p className="text-[#5A5E80] text-base sm:text-lg leading-relaxed">{intro}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {formats.map((f, i) => (
            <div
              key={i}
              className="rounded-2xl overflow-hidden border border-[#DCDDEE] bg-white"
              style={{ borderTopWidth: 5, borderTopColor: f.borderColor, borderTopStyle: "solid" }}
            >
              <ImagePlaceholder alt={f.title} className="rounded-none" />
              <div className="p-6">
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white mb-3"
                  style={{ background: f.borderColor }}
                >
                  <f.icon className="w-3.5 h-3.5" />
                  Format
                </span>
                <h3 className="font-sans font-semibold text-[#262A7A] text-lg mb-2">{f.title}</h3>
                <p className="text-[#5A5E80] text-sm leading-relaxed">{f.description}</p>
              </div>
            </div>
          ))}
        </div>

        {after && after.map((p, i) => (
          <p key={i} className="text-[#5A5E80] text-sm sm:text-base leading-relaxed mt-6 max-w-3xl">{p}</p>
        ))}
      </div>
    </section>
  );
}

// ─── Chips / Browse Section ───────────────────────────────────────────────────
export interface ChipsGroup {
  icon: LucideIcon;
  title: string;
  chips: { label: string; to: string }[];
  /** Photo across the top of the group card; placeholder when unset */
  image?: SectionImageData;
}

export function ChipsSection({
  id, title, intro, groups, after, shaded = false, image,
}: {
  id?: string;
  title: string;
  intro?: string;
  groups: ChipsGroup[];
  after?: ReactNode[];
  shaded?: boolean;
  /** Photo beside the heading and intro */
  image?: SectionImageData;
}) {
  return (
    <section id={id} className={cn("py-14 sm:py-[88px] scroll-mt-28", shaded ? "bg-[hsl(237,52%,97%)]" : "bg-background")}>
      <div className="container mx-auto px-4">
        <div className={cn("mb-10", image ? "grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center" : "max-w-2xl")}>
          <div>
            <h2 className="font-sans text-2xl sm:text-4xl text-[#262A7A] mb-4">{title}</h2>
            {intro && <p className="text-[#5A5E80] text-base sm:text-lg leading-relaxed">{intro}</p>}
          </div>
          {image && <SectionImage image={image} />}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {groups.map((g, i) => (
            <div key={i} className="rounded-2xl border border-[#DCDDEE] bg-white overflow-hidden">
              {g.image
                ? <SectionImage image={g.image} className="rounded-none" />
                : <ImagePlaceholder alt={g.title} className="rounded-none" small />}
              <div className="p-5">
                <h3 className="font-sans font-semibold text-[#262A7A] text-base mb-3 flex items-center gap-2">
                  <g.icon className="w-4 h-4 text-[hsl(237,52%,40%)]" />
                  {g.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {g.chips.map((chip) => (
                    <Link
                      key={chip.label}
                      to={chip.to}
                      className="inline-flex items-center border-1.5 border-[hsl(237,52%,31%)] border rounded-full px-3 py-1 text-sm font-medium text-[hsl(237,52%,31%)] hover:bg-[hsl(237,52%,31%)] hover:text-white transition-colors"
                    >
                      {chip.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {after && after.map((p, i) => (
          <p key={i} className="text-[#5A5E80] text-sm sm:text-base leading-relaxed mt-6 max-w-3xl">{p}</p>
        ))}
      </div>
    </section>
  );
}

// ─── CTA Block ────────────────────────────────────────────────────────────────
export function CtaBlock({ title, body, cta, image }: {
  title: string;
  body: string;
  cta: CtaLink;
  /** Real photo on the right; placeholder when unset */
  image?: SectionImageData;
}) {
  return (
    <section className="py-8 sm:py-12 relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="relative rounded-2xl bg-[hsl(237,52%,25%)] overflow-hidden px-8 sm:px-14 py-12 sm:py-16">
          {/* Decorative rings */}
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full border-[18px] border-white/10 pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full border-[14px] border-white/8 pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.4fr_0.6fr] gap-8 items-center">
            <div>
              <h2 className="font-sans text-3xl sm:text-4xl text-white mb-4">{title}</h2>
              <p className="text-[hsl(237,20%,75%)] text-base sm:text-lg leading-relaxed mb-6">{body}</p>
              <Button asChild size="lg" className="rounded-full px-10 bg-[hsl(39,96%,48%)] hover:bg-[hsl(39,96%,40%)] text-[hsl(237,52%,20%)] font-semibold shadow-lg">
                <Link to={cta.to}>
                  {cta.label}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
            {/* Image on right */}
            <div className="hidden lg:block">
              {image ? <SectionImage image={image} /> : <ImagePlaceholder alt={title} className="opacity-60" />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FAQ Section (two-column: sticky CTA left, accordion right) ───────────────
export function FaqSection({ faqs, cta, title = "Frequently Asked Questions (FAQs)", image }: {
  faqs: FaqItem[];
  cta: CtaLink;
  title?: string;
  /** Real photo under the CTA; placeholder when unset */
  image?: SectionImageData;
}) {
  return (
    <section className="py-14 sm:py-[88px] bg-[hsl(237,52%,97%)]">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16 items-start">

          {/* Left — sticky CTA */}
          <div className="lg:sticky lg:top-44">
            <Eyebrow>Common questions</Eyebrow>
            <h2 className="font-sans text-2xl sm:text-3xl text-[#262A7A] mb-4">{title}</h2>
            {image && <SectionImage image={image} className="mb-5" />}
            <Button asChild size="lg" className={cn(PRIMARY_BTN, "mt-2")}>
              <Link to={cta.to}>
                {cta.label}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            {!image && (
              <div className="hidden lg:block mt-8">
                <ImagePlaceholder alt="FAQ" small />
              </div>
            )}
          </div>

          {/* Right — accordion */}
          <div className="flex flex-col gap-2 sm:gap-3">
            {faqs.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-[#DCDDEE] bg-white overflow-hidden">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 sm:px-6 py-4 hover:bg-[hsl(237,52%,97%)] transition-colors [&::-webkit-details-marker]:hidden">
                  <h3 className="text-sm font-semibold text-[#262A7A]">{`Q${i + 1}. ${f.question}`}</h3>
                  <span className="w-6 h-6 rounded-full border border-[#DCDDEE] flex items-center justify-center flex-shrink-0 text-[#5A5E80] text-lg font-light leading-none transition-transform duration-300 group-open:rotate-45 group-open:border-[hsl(237,52%,40%)] group-open:text-[hsl(237,52%,40%)]">
                    +
                  </span>
                </summary>
                <p className="px-4 sm:px-6 pb-4 sm:pb-5 pt-3 sm:pt-4 text-sm text-[#5A5E80] leading-relaxed border-t border-[#DCDDEE]">
                  {f.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Related Pages Section ────────────────────────────────────────────────────
export function RelatedPages({ pages }: { pages: RelatedPage[] }) {
  if (pages.length === 0) return null;
  return (
    <section className="py-12 sm:py-16 bg-background border-t border-[#DCDDEE]">
      <div className="container mx-auto px-4">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-6">Also on BookMyCoaching</p>
        <div className={cn(
          "grid gap-5",
          pages.length === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 md:grid-cols-3",
        )}>
          {pages.map((page) => {
            const PageIcon = page.icon;
            return (
              <Link
                key={page.to}
                to={page.to}
                className="group rounded-2xl border border-[#DCDDEE] bg-white p-6 hover:border-[hsl(237,52%,40%)] hover:shadow-md transition-all"
                style={{ borderTopWidth: 4, borderTopColor: page.color, borderTopStyle: "solid" }}
              >
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white px-2.5 py-1 rounded-full mb-3"
                  style={{ background: page.color }}
                >
                  <PageIcon className="w-3.5 h-3.5" />
                  {page.label}
                </span>
                <h3 className="font-sans font-semibold text-[#262A7A] text-base leading-snug mb-2 group-hover:text-[hsl(237,52%,31%)] transition-colors">
                  {page.title}
                </h3>
                <p className="text-[#5A5E80] text-sm leading-relaxed">{page.excerpt}</p>
                <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-[hsl(237,52%,40%)]">
                  Learn more <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Image Placeholder ────────────────────────────────────────────────────────
/** A delivered photo, at its own aspect ratio */
export function SectionImage({ image, className }: { image: SectionImageData; className?: string }) {
  return (
    <img
      src={image.src}
      alt={image.alt}
      width={image.width}
      height={image.height}
      loading="lazy"
      className={cn("w-full h-auto rounded-2xl", className)}
    />
  );
}

/** Dashed placeholder used while real images are pending from the design team */
export function ImagePlaceholder({ alt, className, small }: { alt?: string; className?: string; small?: boolean }) {
  return (
    <figure
      className={cn(
        "relative w-full bg-[repeating-linear-gradient(135deg,rgba(38,42,122,0.06)_0_12px,rgba(38,42,122,0.02)_12px_24px),#E3E4F4]",
        "border-2 border-dashed border-[hsl(237,30%,70%)] rounded-2xl overflow-hidden flex items-end",
        small ? "aspect-[16/7]" : "aspect-[4/3]",
        className,
      )}
      role="img"
      aria-label={alt ?? "Image coming soon"}
    >
      <figcaption className="bg-white/90 m-3 px-3 py-2 rounded-xl text-[11px] text-[#5A5E80] leading-relaxed max-w-xs">
        <span className="inline-block bg-[hsl(237,52%,31%)] text-[hsl(39,96%,55%)] text-[10px] font-bold px-1.5 py-0.5 rounded mb-1">
          IMG
        </span>
        <br />
        <strong>Show:</strong> {alt ?? "Image to be provided by design team"}
      </figcaption>
    </figure>
  );
}
